import { Dialog, DialogContent, DialogTitle, IconButton } from '@mui/material';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { type output as ZodOutput } from 'zod';

import {
  type ImportLedgerAccountsPayload,
  type ImportLedgerAccountsResponse,
  useMutationImportLedgerAccounts,
} from '~/data/ledgerAccount';

import { Icon } from '@vestigas/ui';
import { ErrorBoundary } from '@vestigas/ui/atoms';
import {
  DataImportPreviewTable,
  DataImportWizard,
  type DataImportWizardInput,
  type DataImportWizardPreviewRenderContext,
  type DataImportWizardValidationInput,
  type DataImportPreviewRemoteIssue,
  useDataImportCopy,
} from '@vestigas/ui/organisms';
import {
  HttpValidationErrorAlert,
  pickHttpValidationError,
} from '~/ui/molecules';

import { useLedgerAccountImportConfig } from './ledgerAccountImportConfig';
import {
  LedgerAccountImportPreview,
  LedgerAccountImportValidationAlert,
} from './LedgerAccountImportPreview';
import { ledgerAccountImportHeaderFields } from './ledgerAccountImportSchema';
import {
  zodLedgerAccountImportFormSchema,
  zodLedgerAccountImportRowsSchema,
} from './validations';

type P = {
  readonly isOpen: boolean;
  readonly onClose: () => void;
};

type LedgerAccountImportInput = DataImportWizardInput<
  ZodOutput<typeof zodLedgerAccountImportRowsSchema>
>;
type LedgerAccountImportValidationInput = DataImportWizardValidationInput<
  ZodOutput<typeof zodLedgerAccountImportRowsSchema>
>;

type LedgerAccountImportField =
  (typeof ledgerAccountImportHeaderFields)[number];

function isLedgerAccountImportField(
  field: string,
): field is LedgerAccountImportField {
  return ledgerAccountImportHeaderFields.some(
    (importField) => importField === field,
  );
}

function toColumnNamesMap(columnMapping?: Record<string, string>) {
  return Object.entries(columnMapping ?? {}).map(([nameInFile, expected]) => {
    if (!isLedgerAccountImportField(expected)) {
      throw new Error(`Unsupported ledger account import field: ${expected}`);
    }

    return {
      expected,
      nameInFile,
    };
  });
}

function createImportPayload(
  { columnMapping, file, formData }: LedgerAccountImportValidationInput,
  isDryRun: boolean,
): ImportLedgerAccountsPayload {
  const { applyToAllCompanies, companyIds, validFrom } =
    zodLedgerAccountImportFormSchema.parse(formData);

  return {
    applyToAllCompaniesInAccount: applyToAllCompanies,
    columnNamesMap: toColumnNamesMap(columnMapping),
    companyIds: applyToAllCompanies ? null : (companyIds ?? null),
    file,
    isDryRun,
    validFrom: validFrom || null,
  };
}

function renderPreview({
  copy,
  model,
  remotePreview,
}: DataImportWizardPreviewRenderContext<ImportLedgerAccountsResponse>) {
  const hasValidationErrors = model.issues.some(
    (issue) => issue.severity === 'error',
  );

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-6 overflow-hidden">
      {remotePreview.status === 'ready' ? (
        <LedgerAccountImportPreview
          hasValidationErrors={hasValidationErrors}
          result={remotePreview.value}
          variant="preview"
        />
      ) : hasValidationErrors ? (
        <LedgerAccountImportValidationAlert hasValidationErrors />
      ) : null}
      <DataImportPreviewTable
        copy={copy}
        model={model}
        showGlobalIssues={false}
      />
    </div>
  );
}

function renderResult(result: ImportLedgerAccountsResponse) {
  return <LedgerAccountImportPreview result={result} variant="result" />;
}

function renderSubmitError(error: Error) {
  const httpError = pickHttpValidationError(error);
  const status = httpError?.response.status;

  if (status !== undefined && status >= 500 && status < 600) {
    return null;
  }

  return <HttpValidationErrorAlert error={httpError} isSubmitting={false} />;
}

function CloseDialogButton({
  ariaLabel,
  onClose,
}: {
  readonly ariaLabel: string;
  readonly onClose: () => void;
}) {
  return (
    <IconButton aria-label={ariaLabel} size="small" onClick={onClose}>
      <Icon metaphor="close" />
    </IconButton>
  );
}

export function LedgerAccountImportDialog({ isOpen, onClose }: P) {
  const { t } = useTranslation('ledgerAccounts');
  const { wizardFallback } = useDataImportCopy();

  const config = useLedgerAccountImportConfig();

  const { mutateAsync: importLedgerAccounts } =
    useMutationImportLedgerAccounts();

  const validateLedgerAccountImport = useCallback(
    (input: LedgerAccountImportValidationInput) =>
      importLedgerAccounts(createImportPayload(input, true)),
    [importLedgerAccounts],
  );

  const confirmLedgerAccountImport = useCallback(
    (input: LedgerAccountImportInput) =>
      importLedgerAccounts(createImportPayload(input, false)),
    [importLedgerAccounts],
  );

  const getRemoteIssues = useCallback(
    (
      preview: ImportLedgerAccountsResponse,
    ): readonly DataImportPreviewRemoteIssue[] =>
      preview.skippedInvalid > 0
        ? [
            {
              message: t(($) => $.import.preview.invalidRecordsIssue),
              severity: 'error',
              source: 'remote',
            },
          ]
        : [],
    [t],
  );

  return (
    <Dialog
      disableEscapeKeyDown
      fullWidth
      maxWidth="md"
      open={isOpen}
      slotProps={{ paper: { className: 'h-full' } }}
      onClose={onClose}
    >
      <DialogTitle className="flex items-center justify-between">
        {t(($) => $.import.title)}
        <CloseDialogButton
          ariaLabel={t(($) => $.import.closeButton.ariaLabel)}
          onClose={onClose}
        />
      </DialogTitle>
      <DialogContent className="flex min-h-0 flex-1 flex-col overflow-hidden">
        <ErrorBoundary fallbackMessage={wizardFallback}>
          <DataImportWizard
            config={config}
            getRemoteIssues={getRemoteIssues}
            onCancel={onClose}
            onImport={confirmLedgerAccountImport}
            onValidate={validateLedgerAccountImport}
            renderPreview={renderPreview}
            renderResult={renderResult}
            renderSubmitError={renderSubmitError}
          />
        </ErrorBoundary>
      </DialogContent>
    </Dialog>
  );
}
